import React, { useState, useCallback, useEffect } from 'react';
import { Col, Card, CardBody, Container, Row, Table, Input, Button } from 'reactstrap';
import { Btn } from '../../AbstractElements';
import { ToastContainer, toast } from 'react-toastify';
import {
  useLazyGetFormulaByReferenceQuery,
  useSaveAdminFormulaMutation,
  useGetIngredientsCodesQuery,
  useGetIngredientPricesQuery,
  useGetIngredientsListQuery,
} from '../../features/ordersApiSlice';

const PRICE_PER_MG_ACTIVE = [
  { id: 1, pricePerMgActive: 0.022 }, // old price: 0.041995 (Ashwagandha)
  { id: 25, pricePerMgActive: 8.11512 },
  { id: 4, pricePerMgActive: 0.01917 },
  { id: 5, pricePerMgActive: 69.13318684 },
  { id: 6, pricePerMgActive: 0.054 }, // old price: 0.1215 (CoQ10)
  { id: 7, pricePerMgActive: 6.267857143 },
  { id: 11, pricePerMgActive: 0.07626887403 },
  { id: 12, pricePerMgActive: 0.03, }, // old price: 0.04729411765 (Magnesium)
  { id: 13, pricePerMgActive: 2.678787879 },
  { id: 14, pricePerMgActive: 0.007 }, // old price: 0.01182857143 (MSM)
  { id: 15, pricePerMgActive: 0.01944 },
  { id: 17, pricePerMgActive: 28.6416 },
  { id: 18, pricePerMgActive: 4.05756 },
  { id: 20, pricePerMgActive: 8.11512 },
  { id: 21, pricePerMgActive: 0.202878 },
  { id: 24, pricePerMgActive: 0.202878 },
  { id: 26, pricePerMgActive: 0.86904 },
  { id: 27, pricePerMgActive: 0.011 }, // old price: 0.01908 (Vitamin C)
  { id: 28, pricePerMgActive: 33.813 }, // old price: 50.72 (Vitamin D)
  { id: 29, pricePerMgActive: 0.052 },
  { id: 30, pricePerMgActive: 8.11512 },
  { id: 19, pricePerMgActive: 0.144 }, // old price: 0.202878 (Vitamin B1)
  { id: 22, pricePerMgActive: 0.0473382 },
  { id: 23, pricePerMgActive: 0.202878 },
  { id: 16, pricePerMgActive: 0.013 }, // old price: 0.01801983079 (Potassium)
  { id: 8, pricePerMgActive: 0.022365 },
  { id: 31, pricePerMgActive: 0.175 }, // old price: 0.2338246253 (Zinc)
  { id: 33, pricePerMgActive: 0.86904 },
  { id: 34, pricePerMgActive: 8.11512 },
  { id: 35, pricePerMgActive: 0.01690911 },
  { id: 36, pricePerMgActive: 0.0408 },
  { id: 37, pricePerMgActive: 15.34090909 },
  { id: 39, pricePerMgActive: 0.03 }, // SHI-554: Magnesium (Bisglycinate), priced as citrate (id 12)
];

const s = {
  th: { padding: '4px 6px', fontSize: '12px', whiteSpace: 'nowrap', verticalAlign: 'middle' },
  td: { padding: '2px 6px', fontSize: '12px', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  input: { width: '80px', fontSize: '12px', padding: '2px 4px', height: '26px' },
  muted: { fontSize: '10px', color: '#999', marginTop: '1px' },
};

const FormulaEdit = () => {
  const [orderId, setOrderId] = useState('');
  const [quizId, setQuizId] = useState('');
  const [formulaData, setFormulaData] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [selectedToAdd, setSelectedToAdd] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [updatePriceOnStripe, setUpdatePriceOnStripe] = useState(false);
  const [isLoadingFormulaRequest, setIsLoadingFormulaRequest] = useState(false);

  const [getFormula, { isLoading: isLoadingFormula, isFetching: isFetchingFormula }] = useLazyGetFormulaByReferenceQuery();
  const [saveFormula, { isLoading: isSaving }] = useSaveAdminFormulaMutation();
  const { data: allIngredientCodes } = useGetIngredientsCodesQuery();
  const { data: ingredientPrices } = useGetIngredientPricesQuery('en');
  const { data: ingredientsList } = useGetIngredientsListQuery();
  const totalPrice = ingredients.reduce((sum, ing) => sum + (ing.customerPrice || 0), 0);
  const stripePricing = formulaData?.stripePricing || null;
  const subscriptionCadenceMonths = Number(stripePricing?.cadenceMonths || 0);
  const stripeUnitPrice =
    subscriptionCadenceMonths === 3
      ? totalPrice * 0.7
      : subscriptionCadenceMonths === 1
        ? totalPrice * 0.9
        : totalPrice;
  const displayStripeTargetPrice =
    subscriptionCadenceMonths === 3
      ? stripeUnitPrice * 3
      : stripeUnitPrice;
  const roundedTotalPrice = Math.round(displayStripeTargetPrice || 0);
  const roundedStripeUnitPrice = Math.round(stripeUnitPrice || 0);
  const currentRoundedStripePrice = formulaData?.stripePricing?.roundedCurrentPrice ?? null;
  const canUpdateStripePrice = Boolean(formulaData?.stripePricing?.canUpdateStripePrice);
  const isFormulaLoading = isLoadingFormulaRequest || isLoadingFormula || isFetchingFormula;

  const getLimit = useCallback(
    (ingredientId) => {
      const ingCode = allIngredientCodes?.data?.find((i) => i.id === ingredientId);
      return ingCode ? parseFloat(ingCode.limit_riise) || 0 : 0;
    },
    [allIngredientCodes]
  );

  const recalculate = useCallback(
    (ingredientId, dailyDose) => {
      const dose = parseFloat(dailyDose) || 0;
      const ingCode = allIngredientCodes?.data?.find((i) => i.id === ingredientId);
      const price = ingredientPrices?.data?.find((i) => i.ingredient_id === ingredientId);

      let per_eu_nrv = 0;
      if (ingCode && parseFloat(ingCode.eu_nrv) > 0) {
        per_eu_nrv = Math.round((dose / parseFloat(ingCode.eu_nrv)) * 100);
      }

      let cogs = 0;
      if (price && parseFloat(price.active_percentage) > 0) {
        cogs = parseFloat(((dose / parseFloat(price.active_percentage)) * parseFloat(price.quote_per_mg)).toFixed(6));
      }

      // Customer price: (dose * pricePerMgActive + 0.5 labor) * 1.17 commission * 1.07 VAT
      let customerPrice = 0;
      const priceLookup = PRICE_PER_MG_ACTIVE.find((p) => p.id === ingredientId);
      if (priceLookup) {
        customerPrice = parseFloat(((dose * priceLookup.pricePerMgActive + 0.5) * 1.17 * 1.07).toFixed(2)); // old labo cost: 0.6
      }

      return { per_eu_nrv, cogs, customerPrice };
    },
    [allIngredientCodes, ingredientPrices]
  );

  const buildIngredientRow = useCallback(
    (ing) => {
      const { per_eu_nrv, cogs, customerPrice } = recalculate(ing.ingredient_id, ing.daily_dose);

      return {
        ingredient_id: ing.ingredient_id,
        ingredient: ing.ingredient,
        daily_dose: ing.daily_dose || '',
        per_eu_nrv,
        cogs,
        customerPrice,
      };
    },
    [recalculate]
  );

  useEffect(() => {
    setIngredients((prev) => {
      if (!prev.length) return prev;

      let hasChanges = false;
      const updated = prev.map((ing) => {
        const next = buildIngredientRow(ing);
        const didChange =
          Number(ing.per_eu_nrv || 0) !== Number(next.per_eu_nrv || 0) ||
          Number(ing.cogs || 0) !== Number(next.cogs || 0) ||
          Number(ing.customerPrice || 0) !== Number(next.customerPrice || 0);

        if (didChange) {
          hasChanges = true;
          return next;
        }

        return ing;
      });

      return hasChanges ? updated : prev;
    });
  }, [buildIngredientRow]);

  const handleLoad = async () => {
    const trimmedOrderId = orderId.trim();
    const trimmedQuizId = quizId.trim();

    if (!trimmedOrderId && !trimmedQuizId) {
      toast.error('Please enter an Order ID or quizId');
      return;
    }

    setIsLoadingFormulaRequest(true);
    try {
      const res = await getFormula({
        orderId: trimmedOrderId || undefined,
        quizId: trimmedQuizId || undefined,
      }).unwrap();
      setFormulaData(res);
      setUpdatePriceOnStripe(false);
      setIngredients((res.ingredients || []).map(buildIngredientRow));
    } catch (err) {
      console.error('Error loading formula:', err);
      toast.error(err?.data?.message || 'Error loading formula');
    } finally {
      setIsLoadingFormulaRequest(false);
    }
  };

  const handleDoseChange = (index, value) => {
    setIngredients((prev) => {
      const updated = [...prev];
      const ing = { ...updated[index], daily_dose: value };
      const { per_eu_nrv, cogs, customerPrice } = recalculate(ing.ingredient_id, value);
      ing.per_eu_nrv = per_eu_nrv;
      ing.cogs = cogs;
      ing.customerPrice = customerPrice;
      updated[index] = ing;
      return updated;
    });
  };

  const handleRemoveIngredient = (index) => {
    setIngredients((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleSelectToAdd = (id) => {
    setSelectedToAdd((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAddIngredients = () => {
    const ids = Object.keys(selectedToAdd).filter((k) => selectedToAdd[k]).map(Number);
    if (ids.length === 0) return;

    const newItems = ids
      .map((id) => {
        const found = ingredientsList?.data?.find((ing) => ing.id === id);
        if (!found) return null;
        return buildIngredientRow({
          ingredient_id: found.id,
          ingredient: found.ingredient,
          daily_dose: '',
        });
      })
      .filter(Boolean);

    setIngredients((prev) => [...prev, ...newItems]);
    setSelectedToAdd({});
  };

  const selectedCount = Object.values(selectedToAdd).filter(Boolean).length;

  const handleSave = async () => {
    if (!formulaData) return;

    const trimmedOrderId = orderId.trim();
    const trimmedQuizId = quizId.trim();
    const resolvedCustomerQuizId = parseInt(formulaData.customerQuizId, 10);

    if (!trimmedOrderId && !resolvedCustomerQuizId) {
      toast.error('Load a formula before saving.');
      return;
    }

    const payload = {
      ...(trimmedOrderId ? { orderId: parseInt(trimmedOrderId, 10) } : {}),
      ...(!trimmedOrderId && trimmedQuizId ? { quizId: parseInt(trimmedQuizId, 10) } : {}),
      ...(!trimmedOrderId && resolvedCustomerQuizId ? { customerQuizId: resolvedCustomerQuizId } : {}),
      ingredients: ingredients.map((ing) => ({
        ingredient_id: ing.ingredient_id,
        daily_dose: ing.daily_dose || 0,
        per_eu_nrv: ing.per_eu_nrv || 0,
        cogs: ing.cogs || null,
      })),
      updatePriceOnStripe,
      ...(updatePriceOnStripe ? { formulaPrice: roundedStripeUnitPrice } : {}),
    };

    try {
      const res = await saveFormula(payload).unwrap();
      const scheduledStripeAmount =
        subscriptionCadenceMonths === 3
          ? roundedStripeUnitPrice * 3
          : roundedStripeUnitPrice;
      const stripeMessage =
        res?.stripePriceChange?.status === 'scheduled'
          ? ` Stripe update scheduled for ${new Date(res.stripePriceChange.effectiveAt).toLocaleDateString()} at ${scheduledStripeAmount}€.`
          : res?.stripePriceChange?.reason
            ? ` Stripe: ${res.stripePriceChange.reason}`
            : '';
      toast.success(`Formula saved! New Customer Quiz ID: ${res.newCustomerQuizId}.${stripeMessage}`);
    } catch (err) {
      console.error('Error saving formula:', err);
      toast.error(err?.data?.message || 'Error saving formula');
    }
  };

  const availableIngredients = (ingredientsList?.data || []).filter(
    // SHI-583: this edits riise customer formulas, so D2C-disabled ingredients are not offered.
    (ing) => ing.active === 'Yes' && Number(ing.d2c ?? 1) !== 0 && !ingredients.some((existing) => existing.ingredient_id === ing.id)
  );

  return (
    <Container fluid={true} style={{ paddingTop: '20px' }}>
      <Row>
        <Col sm="12" lg="10" xl="9">
          <Card>
            <CardBody>
              <h5 className="mb-3">Formula Edit</h5>
              <Row className="mb-3 align-items-center">
                <Col sm="3">
                  <Input
                    type="text"
                    bsSize="sm"
                    placeholder="Order ID"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleLoad(); }}
                  />
                </Col>
                <Col sm="3">
                  <Input
                    type="text"
                    bsSize="sm"
                    placeholder="quizId"
                    value={quizId}
                    onChange={(e) => setQuizId(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleLoad(); }}
                  />
                </Col>
                <Col sm="2">
                  <Btn attrBtn={{ color: 'primary', size: 'sm', onClick: handleLoad, disabled: isFormulaLoading }}>
                    {isFormulaLoading ? 'Loading...' : 'Load'}
                  </Btn>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col sm="8">
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    quizId can be found on omnisend and in incomplete_quiz in the db. It will load the active quiz where is_admin = 1
                  </span>
                </Col>
              </Row>

              {formulaData && (
                <>
                  <Row className="mb-2 align-items-center">
                    <Col>
                      <span style={{ fontSize: '13px' }}>
                        <strong>Customer:</strong> {formulaData.userName} ({formulaData.userEmail})
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        <strong>Order:</strong> {formulaData.orderId || '-'}
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        <strong>Customer Quiz ID:</strong> {formulaData.customerQuizId}
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        <strong>User:</strong> {formulaData.userId}
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        <strong>Loaded By:</strong> {formulaData.loadedBy}
                      </span>
                    </Col>
                    <Col sm="auto" className="d-flex align-items-center gap-3">
                      <span style={{ fontSize: '14px', fontWeight: 700 }}>
                        Total (30 d): {totalPrice.toFixed(2)} €
                      </span>
                      <Btn attrBtn={{ color: 'success', size: 'sm', onClick: handleSave, disabled: isSaving || ingredients.length === 0 }}>
                        {isSaving ? 'Saving...' : 'Save Formula'}
                      </Btn>
                    </Col>
                  </Row>

                  {canUpdateStripePrice && (
                    <Row className="mb-3">
                      <Col sm="12">
                        <div className="d-flex flex-column gap-2" style={{ fontSize: '13px' }}>
                          <label
                            className="d-flex align-items-center gap-2 mb-0"
                            style={{ cursor: 'pointer' }}
                          >
                            <input
                              type="checkbox"
                              checked={updatePriceOnStripe}
                              onChange={(e) => setUpdatePriceOnStripe(e.target.checked)}
                              style={{
                                width: '16px',
                                height: '16px',
                                accentColor: '#0d6efd',
                                cursor: 'pointer',
                              }}
                            />
                            <span><strong>Update price on Stripe</strong></span>
                          </label>

                          <span style={{ color: '#666' }}>
                            Current Stripe price: <strong>{currentRoundedStripePrice} €</strong>
                            {stripePricing?.productPriceDuration ? ` (${stripePricing.productPriceDuration})` : ''}
                          </span>

                          {updatePriceOnStripe && (
                            <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', padding: '10px 12px' }}>
                              <div>
                                <strong>New Stripe price</strong>: {roundedTotalPrice} €
                              </div>
                              <div style={{ color: '#666', marginTop: '4px' }}>
                                Based on current sub:
                                {' '}
                                {subscriptionCadenceMonths === 3
                                  ? '3 months with 30% discount'
                                  : subscriptionCadenceMonths === 1
                                    ? '1 month with 10% discount'
                                    : 'custom pricing'}
                              </div>
                              <div style={{ color: '#666', marginTop: '4px' }}>
                                Stripe price change: {currentRoundedStripePrice} € -> {roundedTotalPrice} €
                              </div>
                              {/* {subscriptionCadenceMonths === 3 && (
                                <div style={{ color: '#666', marginTop: '4px' }}>
                                  Sent to Stripe as unit price: {roundedStripeUnitPrice} € with quantity 3
                                </div>
                              )} */}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  )}

                  <Row className="mb-2 align-items-start">
                    <Col sm="auto" style={{ position: 'relative' }}>
                      <Button
                        color="info"
                        size="sm"
                        style={{ fontSize: '12px' }}
                        onClick={() => setDropdownOpen((o) => !o)}
                      >
                        + Add Ingredient{selectedCount > 0 ? ` (${selectedCount})` : ''}
                      </Button>
                      {dropdownOpen && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, zIndex: 10, marginTop: '4px',
                          background: '#fff', border: '1px solid #dee2e6', borderRadius: '4px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', width: '260px',
                        }}>
                          <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px 0' }}>
                            {availableIngredients.map((ing) => (
                              <label key={ing.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', margin: 0 }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!selectedToAdd[ing.id]}
                                  onChange={() => toggleSelectToAdd(ing.id)}
                                  style={{ margin: 0 }}
                                />
                                {ing.ingredient}
                              </label>
                            ))}
                            {availableIngredients.length === 0 && (
                              <div style={{ padding: '8px 10px', fontSize: '12px', color: '#999' }}>All ingredients added</div>
                            )}
                          </div>
                          <div style={{ borderTop: '1px solid #eee', padding: '6px 10px', display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                            <Button size="sm" color="light" style={{ fontSize: '11px' }} onClick={() => { setDropdownOpen(false); setSelectedToAdd({}); }}>Cancel</Button>
                            <Button size="sm" color="primary" style={{ fontSize: '11px' }} disabled={selectedCount === 0} onClick={() => { handleAddIngredients(); setDropdownOpen(false); }}>
                              Add{selectedCount > 0 ? ` (${selectedCount})` : ''}
                            </Button>
                          </div>
                        </div>
                      )}
                    </Col>
                  </Row>

                  <Table bordered responsive style={{ fontSize: '12px', marginBottom: 0 }}>
                    <thead className="table-light">
                      <tr>
                        <th style={{ ...s.th, width: '36px' }}>ID</th>
                        <th style={{ ...s.th, width: '140px' }}>Ingredient</th>
                        <th style={{ ...s.th, width: '150px' }}>Daily Dose (mg)</th>
                        <th style={{ ...s.th, width: '65px', textAlign: 'right' }}>%NRV</th>
                        <th style={{ ...s.th, width: '75px', textAlign: 'right' }}>COGS</th>
                        <th style={{ ...s.th, width: '85px', textAlign: 'right' }}>Price (30 d) €</th>
                        <th style={{ ...s.th, width: '32px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ingredients.map((ing, index) => {
                        const limit = getLimit(ing.ingredient_id);
                        return (
                          <tr key={ing.ingredient_id}>
                            <td style={s.td}>{ing.ingredient_id}</td>
                            <td style={s.td}>{ing.ingredient}</td>
                            <td style={s.td}>
                              <div className="d-flex align-items-center gap-1">
                                <Input
                                  type="number"
                                  step="any"
                                  value={ing.daily_dose}
                                  onChange={(e) => handleDoseChange(index, e.target.value)}
                                  bsSize="sm"
                                  style={s.input}
                                />
                                {limit > 0 && <span style={s.muted}>/ {limit}</span>}
                              </div>
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>{ing.per_eu_nrv || '-'}</td>
                            <td style={{ ...s.td, textAlign: 'right' }}>{ing.cogs ? parseFloat(ing.cogs).toFixed(4) : '-'}</td>
                            <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{ing.customerPrice ? `${ing.customerPrice.toFixed(2)}` : '-'}</td>
                            <td style={{ ...s.td, textAlign: 'center' }}>
                              <Button color="danger" size="sm" style={{ padding: '0 5px', fontSize: '11px', lineHeight: '1.4' }} onClick={() => handleRemoveIngredient(index)}>×</Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 700, fontSize: '13px' }}>
                        <td colSpan={4} style={{ ...s.td, textAlign: 'right' }}>Total</td>
                        <td style={{ ...s.td, textAlign: 'right' }}>{ingredients.reduce((sum, ing) => sum + (parseFloat(ing.cogs) || 0), 0).toFixed(4)}</td>
                        <td style={{ ...s.td, textAlign: 'right' }}>{totalPrice.toFixed(2)} €</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </Table>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <ToastContainer />
    </Container>
  );
};

export default FormulaEdit;
